import type { UvMapCanvas } from '@/types/Product';

/**
 * Atlas LOGIQUE d'un produit 3D à plusieurs meshes imprimables.
 *
 * Le BO définit un canevas UV par mesh (`uvmapList[]`, chacun avec son uvmap et
 * ses zones en coordonnées RELATIVES à ce canevas, `left/top` du canevas = 0).
 * Tout le FO, lui, travaille dans UN seul espace : le canvas Fabric principal
 * (`#texture`), où les éléments et les zones sont en coordonnées absolues, et UNE
 * seule texture Three.js.
 *
 * Plutôt que de dédoubler ce pipeline par mesh, on juxtapose les canevas
 * horizontalement dans le canvas principal (même principe que l'atlas logique des
 * produits 2D, cf. `two-d/surfaces.ts`) : le canevas i est décalé de la somme des
 * largeurs précédentes, ses zones avec lui. Côté 3D, chaque mesh échantillonne sa
 * tranche de la texture (`atlasUvTransform`).
 *
 * Un produit à UN seul canevas n'est pas touché : décalage nul, liste rendue telle
 * quelle — aucun changement de comportement pour l'existant.
 */

export interface AtlasSize {
    width: number;
    height: number;
}

/** Transformée UV → UV d'atlas : `atlasUv = offset + scale * uv`. */
export interface AtlasUvTransform {
    scale: [number, number];
    offset: [number, number];
}

const IDENTITY: AtlasUvTransform = { scale: [1, 1], offset: [0, 0] };

/** Juxtapose les canevas et décale leurs zones. Identité sous deux canevas. */
export function layoutUvAtlases(list: UvMapCanvas[]): UvMapCanvas[] {
    if (list.length <= 1) {
        return list;
    }
    let offsetX = 0;
    return list.map((canvas) => {
        const placed: UvMapCanvas = {
            ...canvas,
            // Coordonnées d'atlas portées par `atlasOffsetX/Y`. `left/top` à 0 : ils
            // ne désignent pas une position dans l'atlas (cf. getAbsoluteZoneBounds,
            // qui les ajouterait aux zones jugées relatives).
            left: 0,
            top: 0,
            atlasOffsetX: offsetX,
            atlasOffsetY: 0,
            zones: canvas.zones.map((zone) => ({ ...zone, left: zone.left + offsetX }))
        };
        offsetX += canvas.width;
        return placed;
    });
}

/** Taille du canvas principal : largeurs cumulées, plus grande hauteur. */
export function atlasSize(list: UvMapCanvas[]): AtlasSize | null {
    if (list.length === 0) {
        return null;
    }
    return list.reduce(
        (size, canvas) => ({
            width: Math.max(size.width, (canvas.atlasOffsetX ?? 0) + canvas.width),
            height: Math.max(size.height, (canvas.atlasOffsetY ?? 0) + canvas.height)
        }),
        { width: 0, height: 0 }
    );
}

/**
 * Tranche d'atlas d'un canevas, en UV de la texture (v vers le HAUT, la texture
 * canvas étant retournée : pixel y = H − v·H). Un canevas qui couvre tout l'atlas
 * donne l'identité.
 */
export function atlasUvTransform(canvas: UvMapCanvas, size: AtlasSize): AtlasUvTransform {
    if (!size.width || !size.height) {
        return IDENTITY;
    }
    const offsetX = canvas.atlasOffsetX ?? 0;
    const offsetY = canvas.atlasOffsetY ?? 0;
    return {
        scale: [canvas.width / size.width, canvas.height / size.height],
        offset: [offsetX / size.width, (size.height - offsetY - canvas.height) / size.height]
    };
}

export function isIdentityTransform(transform: AtlasUvTransform): boolean {
    return (
        transform.scale[0] === 1 && transform.scale[1] === 1 && transform.offset[0] === 0 && transform.offset[1] === 0
    );
}

/**
 * UV d'un mesh → UV d'atlas. Miroir CPU du fragment shader (raycast) : hors de sa
 * tranche, u boucle (RepeatWrapping de la texture) et v est borné (ClampToEdge),
 * pour ne jamais déborder sur la tranche d'un autre mesh.
 */
export function toAtlasUv(uv: { x: number; y: number }, transform: AtlasUvTransform): { x: number; y: number } {
    if (isIdentityTransform(transform)) {
        return { x: uv.x, y: uv.y };
    }
    const u = uv.x < 0 || uv.x > 1 ? uv.x - Math.floor(uv.x) : uv.x;
    const v = Math.min(Math.max(uv.y, 0), 1);
    return {
        x: transform.offset[0] + transform.scale[0] * u,
        y: transform.offset[1] + transform.scale[1] * v
    };
}
